/**
 * Manage Job edit page — all script logic for `/manage-jobs/edit/:id`, shared via
 * `provide(MANAGE_JOB_EDIT_PAGE_KEY)` so the route shell and layout variants inject the same refs/actions.
 */

// Libraries
import { inject } from 'vue';
import { storeToRefs } from 'pinia';
import { Mask } from 'maska';

// Constants
import cancelJobOptions from '@/constants/cancelJobOptions';
import { notificationKeys } from '@/constants/notificationKeys';
import { jobStates } from '@/constants/manageJobsStatus';
import contactNumbers from '@/constants/operationalContactNumbers';
import notCancellableReasons from '@/constants/notCancellableReasons';

// Stores
import { useManageJobStore } from '@/stores/useManageJob';
import { useNotificationsStore } from '@/stores/useNotifications';
import { useUserDetailsStore } from '@/stores/useUserDetails';

// Components
// Composables
import useAcvDatadog from '@/composables/useAcvDatadog';
import { findStatusObjectByJobStatus } from '@/composables/findStatusObjectByJobStatus';
import { mapPayoutByStatus } from '@/composables/mapPayoutByStatus';
import { dateHelper } from '@/composables/dateHelper';
import { useCarrierVerificationStatus } from '@/composables/useCarrierVerificationStatus';
import { carrierVerificationStatus } from '@/constants/carrierVerificationStatus';

// Utilities
import { isAbortError } from '@/utilities/isAbortError';

/** Injection key for manage-job edit page context (dialogs + content children). */
export const MANAGE_JOB_EDIT_PAGE_KEY = Symbol('manageJobEditPage');

/**
 * Use inside Job/Edit page content components only (child of `[editIdSlug].vue` that calls provide).
 */
export function useManageJobEditPageContext() {
  const ctx = inject(MANAGE_JOB_EDIT_PAGE_KEY);
  if (!ctx) {
    throw new Error(
      '[useManageJobEditPageContext] Missing provider — must render under manage-jobs/edit/[editIdSlug].vue',
    );
  }
  return ctx;
}

export function useManageJobEditPage() {
  const { addRumError, addRumAction } = useAcvDatadog();
  const route = useRoute();
  const router = useRouter();
  const jobId = route.params.editIdSlug;

  const manageJobStore = useManageJobStore();

  const {
    requestId,
    status,
    selectedJob,
    language,
    billOfLadingUrl,
    datePickupActual,
    dateDispatched,
    dateSLALimit,
    associatedJobs,
    isBackhaul,
    isLoading,
    notification,
  } = storeToRefs(manageJobStore);

  const selectedLanguage = ref('English');

  const userDetailsStore = useUserDetailsStore();
  const { carrier } = storeToRefs(userDetailsStore);
  const mask = new Mask({ mask: '(###) ###-####' });
  const carrierNumber = carrier.value.amPhone ? mask.masked(carrier.value.amPhone) : '';
  const carrierNumberLink = `tel:${carrier.value.amPhone}`;

  const notificationsStore = useNotificationsStore();
  const { getActiveNotification } = storeToRefs(notificationsStore);

  const isJobDispatched = ref(status.value === 'Accepted');

  const isDeliveryETAButtonVisible = computed(() => {
    return status.value === jobStates.dispatched.value || status.value === jobStates.pickedup.value;
  });

  const isJobPastDueBySLALimit = computed(() => dateHelper().dateDiff(dateSLALimit.value) > 0);

  const ETADialogMessage = ref('');
  const isETADialogVisible = ref(false);
  const jobOrOfferString = ref('job');

  const dialogCancelText = computed(() => {
    return `No, keep ${jobOrOfferString.value}`;
  });

  const dialogSubmitText = computed(() => {
    return `Yes, cancel ${jobOrOfferString.value}`;
  });

  const dialogTitle = computed(() => {
    return `Cancel ${jobOrOfferString.value}?`;
  });

  // trigger a manual reload of page
  const loadJobInBundle = (id) => {
    router.push(`/manage-jobs/edit/${id}`).then(() => router.go());
  };

  /**
   * Call edit job api and handle errors
   * @param {FormData} fd
   */
  const updateJob = async (fd, { onSuccess } = {}) => {
    try {
      await manageJobStore.editJob(fd);
      onSuccess?.();
    } catch (e) {
      const notificationParams = {
        notificationMessageTemplate: e.message,
        variant: 'error',
        key: notificationKeys.errorEditJob,
      };

      notificationsStore.addNotification(notificationParams);
    } finally {
      const notificationParams = {
        notificationMessageTemplate: `<span class="tw-font-bold">Order ID ${
          requestId.value
        }</span> - ${concatenateVehicleInfo(
          selectedJob.value.vehicle,
        )} has been successfully submitted.`,
        variant: 'success',
        key: notificationKeys.successEditJob,
      };

      notificationsStore.addNotification(notificationParams);
    }
  };

  const jobStatusObject = computed(() => {
    const statusObject = findStatusObjectByJobStatus(status.value);

    const title =
      selectedJob.value?.carrierVerificationStatus === carrierVerificationStatus.pending &&
      selectedJob.value?.status !== 'Pending-Offer'
        ? 'Pending Verification'
        : statusObject.title;

    return { ...statusObject, title };
  });

  // TITLE BAR
  const requiresAction = computed(
    () => status.value === 'Accepted' || status.value === 'Picked-Up',
  );

  const buttonText = computed(() =>
    status.value === 'Accepted' ? 'Mark as Picked Up' : 'Mark as Delivered',
  );

  const disableSundays = computed(() => {
    return [
      function (date) {
        return date.getDay() === 0;
      },
    ];
  });

  const pickupDate = computed(() => {
    return dateHelper().formatToMDY(selectedJob.value[jobStatusObject?.value?.pickupDateProperty]);
  });

  const deliveryDate = computed(() => {
    return dateHelper().formatToMDY(
      selectedJob.value[jobStatusObject?.value?.deliveryDateProperty],
    );
  });

  const minDate = computed(() => {
    if (datePickupActual.value) {
      return pickupDate.value;
    }

    return dateDispatched.value;
  });

  const todayInEST = computed(() => {
    return dateHelper().getTodayInEST().toISOString();
  });

  const showCarrierVerificationStatus = computed(() => {
    return (
      selectedJob.value?.carrierVerificationStatus === 'high_value_pending_verification' &&
      selectedJob.value.status !== 'Pending-Offer'
    );
  });

  const { carrierVerificationStatusLabel, isRequireVerification } = useCarrierVerificationStatus(
    selectedJob.value?.carrierVerificationStatus,
    selectedJob.value?.status,
  );

  /**
   * Update job status with a selected pick up/delivery date
   * @param {String} selectedDate
   */
  const handleSelectedDate = async (selectedDate) => {
    const fd = new FormData();
    const utcDateString = dateHelper().formatToUTC(selectedDate);
    const newStatus = status.value === 'Picked-Up' ? 'Delivered' : 'Picked-Up';

    if (status.value === 'Picked-Up') {
      fd.append('status', 'Delivered');
      fd.append('deliveryDate', utcDateString);
    } else {
      fd.append('status', 'Picked-Up');
      fd.append('pickUpDate', utcDateString);
    }

    await updateJob(fd, {
      onSuccess: () =>
        useMixpanelTrack('lb2 status update date changed', {
          order_id: requestId.value,
          previous_status: status.value,
          new_status: newStatus,
          selected_date: utcDateString,
          is_bundle: computedIsBundleJob.value,
          has_backhaul: isBackhaul.value,
          page: 'edit job',
        }),
    });
  };

  const onStatusUpdateButtonClick = () =>
    useMixpanelTrack('lb2 status update button clicked', {
      order_id: requestId.value,
      is_bundle: computedIsBundleJob.value,
      has_backhaul: isBackhaul.value,
      page: 'edit job',
    });

  // `page` property added to all ETA events for page-level context.
  const onETAUpdateButtonClick = () =>
    useMixpanelTrack('lb2 eta update button clicked', {
      order_id: requestId.value,
      sla_limit_date: dateSLALimit.value,
      is_bundle: computedIsBundleJob.value,
      has_backhaul: isBackhaul.value,
      page: 'edit job',
    });

  // Save the new ETA value that the user selected before the user accept the changes
  const tempETADate = ref(null);

  const showETADialog = (ETADate) => {
    if (deliveryDate.value !== ETADate) {
      isETADialogVisible.value = true;
      ETADialogMessage.value = `Are you sure you want to change the delivery date to ${ETADate}?`;
      tempETADate.value = ETADate;
    }
  };

  const closeETADialog = () => {
    isETADialogVisible.value = false;
    tempETADate.value = null;
    useMixpanelTrack('lb2 eta update date canceled', {
      order_id: requestId.value,
      is_bundle: computedIsBundleJob.value,
      has_backhaul: isBackhaul.value,
      page: 'edit job',
    });
  };

  /**
   * Update job status with a selected pick up/delivery date
   * @param {String} selectedDate
   */
  const handleETAUpdate = async () => {
    const fd = new FormData();

    fd.append('dateDeliveryExpected', dateHelper().formatToISO(tempETADate.value));

    const mixpanelProperties = {
      previous_delivery_eta_date: deliveryDate.value,
      new_delivery_eta_date: tempETADate.value,
      sla_limit_date: dateSLALimit.value,
    };

    try {
      await manageJobStore.editJob(fd);

      const notificationParams = {
        notificationMessageTemplate: `Delivery ETA was <span class="tw-font-bold">updated</span>.`,
        variant: 'success',
        key: notificationKeys.successEditJob,
      };

      notificationsStore.addNotification(notificationParams);

      useMixpanelTrack('lb2 eta update date changed', {
        order_id: requestId.value,
        is_bundle: computedIsBundleJob.value,
        has_backhaul: isBackhaul.value,
        page: 'edit job',
        ...mixpanelProperties,
      });
    } catch (e) {
      const notificationParams = {
        notificationMessageTemplate: e.message,
        variant: 'error',
        key: notificationKeys.errorEditJob,
      };

      notificationsStore.addNotification(notificationParams);

      useMixpanelTrack('lb2 eta update button click failed', {
        order_id: requestId.value,
        sla_limit_date: dateSLALimit.value,
        error_message: e.message,
        is_bundle: computedIsBundleJob.value,
        has_backhaul: isBackhaul.value,
        page: 'edit job',
      });
    }

    isETADialogVisible.value = false;
    tempETADate.value = null;
  };

  // VEHICLE INFO

  const mappedPayoutByStatus = computed(() => mapPayoutByStatus(selectedJob.value));

  // INSTRUCTIONS
  const onLanguageChange = (value) => {
    selectedLanguage.value = value;
    manageJobStore.getJobByID(jobId, value);
  };

  // NOTES
  const isNotesSidebarOpen = ref(false);
  const notes = ref('');

  const handleTextEntered = (value) => {
    notes.value = value.trim();
  };

  const toggleNotes = (value) => {
    isNotesSidebarOpen.value = value;
  };

  const onSaveNotesClick = () => {
    if (notes.value) {
      const fd = new FormData();
      fd.append('carrierComment', notes.value);

      updateJob(fd);

      useMixpanelTrack('lb2 update job carrier note submitted', {
        order_id: requestId.value,
      });

      toggleNotes(false);

      notes.value = '';
    }
  };

  // DOCUMENTS
  const formUploadHeader = computed(() =>
    status.value === 'Delivered' ? 'Drag and drop BOL file' : 'Add eBOL when Delivered',
  );

  const formUploadFileLimitCopy = computed(
    () => status.value === 'Delivered' && 'JPEG, PNG, PDF, DOC (up to 15MB)',
  );

  const isUploadDisabled = computed(() => status.value !== 'Delivered');

  const ebolContainerClasses = computed(() => {
    if (isUploadDisabled.value) {
      return 'grid-container__ebol--disabled';
    } else if (billOfLadingUrl?.value) {
      return 'grid-container__ebol--upload-complete';
    }
    return '';
  });
  const alertOpenStatus = ref(false);
  const isLoadingByType = ref('');

  const downloadGatePass = () => {
    window.open(selectedJob.value.gatePassUrl);
  };

  const handleDownload = async (type) => {
    // Check if the download is In Progress to not download again
    if (isLoadingByType.value === type) {
      return;
    }

    isLoadingByType.value = type;

    const URL = selectedLanguage.value
      ? `/api/transport-requests/${jobId}/pdf?requestedLanguage=${selectedLanguage.value.toUpperCase()}`
      : `/api/transport-requests/${jobId}/pdf`;

    const { $api } = useNuxtApp();

    let data = null;
    let error = null;

    try {
      data = await $api(URL, {
        method: 'GET',
        params: {
          type,
        },
      });
    } catch (err) {
      error = err;
      const errorPayload = {
        job_id: jobId,
        request_id: requestId.value,
        document_type: type,
        selected_language: selectedLanguage.value,
        url: URL,
        error_message: err.message,
        error_status: err.status || err.statusCode,
        error,
      };

      if (isAbortError(error)) {
        addRumAction(`Document Download: ${error.cause || error.message}`, errorPayload);
        return;
      }
      addRumError(new Error('Failed to download job document'), errorPayload);
    }

    isLoadingByType.value = '';

    if (error) {
      alertOpenStatus.value = true;
      return;
    }

    const filenameMap = {
      RELEASE_SLIP: 'ACV Release Slip',
      DISPATCH_SHEET: 'Dispatch Sheet',
    };

    const filename = `${requestId.value}-${filenameMap[type]}.pdf`;

    useFileDownload(data, filename);

    useMixpanelTrack('lb2 update job document download clicked', {
      order_id: requestId.value,
      document_downloaded: filename,
    });
  };

  const handleFileUpload = (contents) => {
    try {
      // call upload file in manage job store
      const fd = new FormData();
      fd.append('bolFile', contents[0]);
      manageJobStore.uploadFile(fd);

      useMixpanelTrack('lb2 update job ebol upload clicked', {
        order_id: requestId.value,
      });
    } catch (e) {
      switch (e.status) {
        case 413:
          notificationsStore.addNotification({
            notificationMessageTemplate:
              'File size exceeds 15MB. Please try uploading the file again.',
            variant: 'error',
            key: notificationKeys.errorUploadFile,
          });
          break;

        default:
          const notificationParams = {
            notificationMessageTemplate: e.message,
            variant: 'error',
            key: notificationKeys.errorUploadFile,
          };
          notificationsStore.addNotification(notificationParams);
          break;
      }

      const customError = new Error(`${e.status} ==> failed to upload file`);
      addRumError(customError, { contents });
    }
  };

  const handleFileDelete = async () => {
    try {
      await manageJobStore.deleteFile();

      useMixpanelTrack('lb2 update job ebol delete clicked', {
        order_id: requestId.value,
      });
    } catch (e) {
      const notificationParams = {
        notificationMessageTemplate: e.message,
        variant: 'error',
        key: notificationKeys.errorUploadFile,
      };
      notificationsStore.addNotification(notificationParams);
    }
  };

  // CANCEL JOB
  const isCancelJobSidebarOpen = ref(false);
  const isCancelJobRequestWorking = ref(false);
  const isCancelDialogOpen = ref(false);
  const cancelFormData = reactive({
    selectedReason: '',
    notes: '',
  });

  const computedIsBundleJob = computed(() => !!selectedJob.value?.bundleId);
  const computedIsJobInvalidStatus = computed(() => {
    return selectedJob.value?.notCancellableReason == notCancellableReasons.invalidStatus;
  });

  const isCancelFormDisabled = computed(() => {
    if (cancelFormData.selectedReason === 'none' || cancelFormData.notes.length === 0) {
      return true;
    }
    if (isCancelJobRequestWorking.value) {
      return true;
    }
  });

  const isCancelableBySelectedStatus = computed(() => status.value === jobStates.pending.value);
  const isNotCancellableNotificationString = ref('');
  const isNotCancellableNotificationKey = ref('');

  const isCancelable = computed(() => {
    switch (selectedJob.value?.notCancellableReason) {
      case notCancellableReasons.invalidStatus:
        return false;
      case notCancellableReasons.bundled:
        isNotCancellableNotificationString.value = `This job is part of a bundle. <span class="tw-font-bold">Call ${contactNumbers.primary} to cancel this job.</span>`;
        isNotCancellableNotificationKey.value = notificationKeys.bundleCancel;
        return false;
      case notCancellableReasons.enclosed:
        isNotCancellableNotificationString.value = `Call <span class="tw-font-bold">${contactNumbers.primary}</span> for questions or to cancel this job.`;
        isNotCancellableNotificationKey.value = notificationKeys.enclosedCancel;
        return false;

      case notCancellableReasons.driveaway:
        isNotCancellableNotificationString.value = `Call <span class="tw-font-bold">${contactNumbers.primary}</span> for questions or to cancel this job.`;
        isNotCancellableNotificationKey.value = notificationKeys.driveawayCancel;
        return false;

      case notCancellableReasons.dispatachedTimeout:
        isNotCancellableNotificationString.value = `It has been over 48 hours since this job was accepted. <span class="tw-font-bold">Call ${contactNumbers.primary} to cancel this job.</span>`;
        isNotCancellableNotificationKey.value = notificationKeys.errorCancelJobTimeout;
        return false;
    }
    if (isCancelableBySelectedStatus.value) {
      jobOrOfferString.value = 'offer';
      return true;
    }

    return dateHelper().isWithin48Hours(dateDispatched.value);
  });

  const toggleCancelOverlay = (value) => {
    if (!isCancelable.value) {
      return;
    }
    isCancelJobSidebarOpen.value = value;
  };

  const handleCancelTextEntered = (value) => {
    cancelFormData.notes = value.trim();
  };

  const onCancelReasonSelected = (value) => {
    cancelFormData.selectedReason = value;
  };

  const redirectBasedOnStatus = async (previousStatus = status.value) => {
    const slug = getSlugByJobStatus(previousStatus);
    await navigateTo(useBackToManageJobs(slug));
  };

  const onCancelJobConfirmClick = async () => {
    const previousStatus = status.value;
    isCancelJobRequestWorking.value = true;

    const canceledJob = await manageJobStore.cancelJob({
      cancelReason: cancelFormData.selectedReason,
      reason: cancelFormData.notes,
    });

    useMixpanelTrack('lb2 update job cancel submitted', {
      order_id: requestId.value,
      cancel_reason: cancelFormData.notes,
    });

    if (canceledJob.cancelReason) {
      redirectBasedOnStatus(previousStatus);
    }
  };

  /**
   * Cancel a pending offer
   * Store the current status before cancel
   * Redirect to corresponding status page (Pending, Dispatched, ...)
   */
  const onCancelPendingOffer = async () => {
    const previousStatus = status.value;
    isCancelJobRequestWorking.value = true;
    try {
      await manageJobStore.cancelOffer();
      redirectBasedOnStatus(previousStatus);
    } catch {
      notificationsStore.addNotification(notification.value);
    }
  };

  const onDialogAccept = () => {
    isCancelDialogOpen.value = false;

    useMixpanelTrack('lb2 manage cancel accept', {
      orderId: requestId.value,
    });

    if (isCancelableBySelectedStatus.value) {
      onCancelPendingOffer();
    } else {
      onCancelJobConfirmClick();
    }
  };

  const openDialog = () => {
    isCancelJobSidebarOpen.value = false;
    isCancelDialogOpen.value = true;

    useMixpanelTrack('lb2 manage cancel open dialog', {
      orderId: requestId.value,
    });
  };

  const closeDialog = () => {
    isCancelDialogOpen.value = false;

    useMixpanelTrack('lb2 manage cancel keep job', {
      orderId: requestId.value,
    });
  };

  const showHVVNotification = () => {
    if (selectedJob.value?.carrierVerificationStatus === carrierVerificationStatus.pending) {
      const HVVNotificationParams = {
        notificationMessageTemplate: `<span class="tw-font-bold">Additional verification is required</span> before the release slip can be downloaded or sensitive information can be displayed. Our team will contact you within the next few business hours.`,
        variant: 'info',
        key: notificationKeys.HVVVerification,
      };
      notificationsStore.addNotification(HVVNotificationParams);
    }
  };

  const checkIfCancellable = () => {
    if (isCancelable.value) {
      if (isCancelableBySelectedStatus.value) {
        // open pending cancel dialog
        openDialog();
      } else {
        // open normal overlay
        toggleCancelOverlay(true);
      }
    } else {
      const notificationMessage = isNotCancellableNotificationString.value;
      const notificationKey = isNotCancellableNotificationKey.value;

      const notificationParams = {
        notificationMessageTemplate: notificationMessage,
        variant: 'error',
        key: notificationKey,
      };
      notificationsStore.addNotification(notificationParams);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const isDeliveredMissingBol = computed(
    () => status.value === 'Delivered' && !billOfLadingUrl.value,
  );

  const isBolChipVisible = computed(() => {
    // We should move this logic to the backend side
    if (isDeliveredMissingBol.value) {
      const DiffInMs =
        new Date().getTime() - new Date(selectedJob.value.dateDeliveryActual).getTime();

      const diffInDays = DiffInMs / (1000 * 60 * 60 * 24);

      return diffInDays <= 30;
    }
    return false;
  });

  const bolChipCopy = computed(() => (selectedJob.value.ebolRequired ? 'No eBol' : 'No Bol'));

  // eslint-disable-next-line no-unused-declarations
  const unsubscribe = notificationsStore.$onAction(({ name, after }) => {
    after((_result) => {
      if (name === 'resetNotifications') {
        showHVVNotification();
      }
    });
  });

  onMounted(async () => {
    showHVVNotification();

    if (status.value === jobStates.finalized.value) {
      const finalizedNotificationParams = {
        notificationMessageTemplate: `<span class="tw-font-bold">Job finalized: Note editing and BOL file changes disabled.</span>`,
        variant: 'info',
        key: notificationKeys.isFinalized,
      };
      notificationsStore.addNotification(finalizedNotificationParams);
    }

    if (isDeliveryETAButtonVisible.value) {
      if (isJobPastDueBySLALimit.value) {
        notificationsStore.addNotification({
          notificationMessageTemplate: `This job is past the acceptable delivery range. <span class="tw-font-bold">Call 716-235-1774 to update the delivery date.</span>`,
          variant: 'caution',
          key: 'jobPastDuebySLADate',
        });

        useMixpanelTrack('lb2 eta job past acceptable delivery range', {
          order_id: requestId.value,
          sla_limit_date: dateSLALimit.value,
          is_bundle: computedIsBundleJob.value,
          has_backhaul: isBackhaul.value,
          page: 'edit job',
        });
      }
    }
  });

  onUnmounted(() => {
    notificationsStore.popNotification(notificationKeys.errorEditJob);
    notificationsStore.popNotification(notificationKeys.successEditJob);
    notificationsStore.popNotification(notificationKeys.errorCancelJobTimeout);
    notificationsStore.popNotification(notificationKeys.bundleCancel);
    notificationsStore.popNotification(notificationKeys.enclosedCancel);
    notificationsStore.popNotification(notificationKeys.jobPastDuebySLADate);
    notificationsStore.popNotification(notificationKeys.HVVVerification);

    manageJobStore.clearSelectedJob();
  });

  watch(
    () => route.params.editIdSlug,
    async (newSlug, oldSlug) => {
      if (!newSlug || newSlug === oldSlug) return;

      isETADialogVisible.value = false;
      tempETADate.value = null;
      ETADialogMessage.value = '';
      isCancelDialogOpen.value = false;
      isCancelJobSidebarOpen.value = false;
      isNotesSidebarOpen.value = false;
      cancelFormData.selectedReason = '';
      cancelFormData.notes = '';

      await manageJobStore.getJobByID(newSlug);
    },
  );

  const useAvailableJobGoogleMap = () => {
    const { isLocationDataAvailable, currentLocationData, populateLocationData } = useGoogleMap();

    populateLocationData([selectedJob.value]);

    const isPickupMapModalOpen = ref(false);
    const isDeliveryMapModalOpen = ref(false);

    const handlePickupToggleModal = () => {
      isPickupMapModalOpen.value = !isPickupMapModalOpen.value;
      useMixpanelTrack('lb2 edit job map link clicked', {
        job_id: jobId,
        is_pickup_map_open: isPickupMapModalOpen.value,
      });
    };

    const handleDeliveryToggleModal = () => {
      isDeliveryMapModalOpen.value = !isDeliveryMapModalOpen.value;
      useMixpanelTrack('lb2 edit job map link clicked', {
        job_id: jobId,
        is_delivery_map_open: isDeliveryMapModalOpen.value,
      });
    };

    return {
      isLocationDataAvailable,
      currentLocationData,
      isPickupMapModalOpen,
      isDeliveryMapModalOpen,
      handlePickupToggleModal,
      handleDeliveryToggleModal,
    };
  };

  const {
    isLocationDataAvailable,
    currentLocationData,
    isPickupMapModalOpen,
    isDeliveryMapModalOpen,
    handlePickupToggleModal,
    handleDeliveryToggleModal,
  } = useAvailableJobGoogleMap();

  // Returned to `[editIdSlug].vue` and content children (inject); keep in sync with both templates.
  return {
    addRumError,
    addRumAction,
    route,
    router,
    jobId,
    manageJobStore,
    requestId,
    status,
    selectedJob,
    language,
    billOfLadingUrl,
    datePickupActual,
    dateDispatched,
    dateSLALimit,
    associatedJobs,
    isBackhaul,
    isLoading,
    notification,
    selectedLanguage,
    carrierNumber,
    carrierNumberLink,
    notificationsStore,
    getActiveNotification,
    isJobDispatched,
    isDeliveryETAButtonVisible,
    isJobPastDueBySLALimit,
    ETADialogMessage,
    isETADialogVisible,
    jobOrOfferString,
    dialogCancelText,
    dialogSubmitText,
    dialogTitle,
    loadJobInBundle,
    updateJob,
    jobStatusObject,
    requiresAction,
    buttonText,
    disableSundays,
    pickupDate,
    deliveryDate,
    minDate,
    todayInEST,
    showCarrierVerificationStatus,
    carrierVerificationStatusLabel,
    isRequireVerification,
    handleSelectedDate,
    onStatusUpdateButtonClick,
    onETAUpdateButtonClick,
    tempETADate,
    showETADialog,
    closeETADialog,
    handleETAUpdate,
    mappedPayoutByStatus,
    onLanguageChange,
    isNotesSidebarOpen,
    notes,
    handleTextEntered,
    toggleNotes,
    onSaveNotesClick,
    formUploadHeader,
    formUploadFileLimitCopy,
    isUploadDisabled,
    ebolContainerClasses,
    alertOpenStatus,
    isLoadingByType,
    downloadGatePass,
    handleDownload,
    handleFileUpload,
    handleFileDelete,
    isCancelJobSidebarOpen,
    isCancelJobRequestWorking,
    isCancelDialogOpen,
    cancelFormData,
    computedIsBundleJob,
    computedIsJobInvalidStatus,
    isCancelFormDisabled,
    isCancelableBySelectedStatus,
    isNotCancellableNotificationString,
    isNotCancellableNotificationKey,
    isCancelable,
    toggleCancelOverlay,
    handleCancelTextEntered,
    onCancelReasonSelected,
    redirectBasedOnStatus,
    onCancelJobConfirmClick,
    onCancelPendingOffer,
    onDialogAccept,
    openDialog,
    closeDialog,
    showHVVNotification,
    checkIfCancellable,
    isLocationDataAvailable,
    currentLocationData,
    isPickupMapModalOpen,
    isDeliveryMapModalOpen,
    handlePickupToggleModal,
    handleDeliveryToggleModal,
    cancelJobOptions,
    isDeliveredMissingBol,
    isBolChipVisible,
    bolChipCopy,
  };
}
